import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Tema = 'dark' | 'light';
const CLAVE = 'tms-tema';
/** Clave anterior (heredada de otro proyecto): se lee una vez para conservar la preferencia. */
const CLAVE_VIEJA = 'austra-theme';

interface TemaCtx {
  tema: Tema;
  alternar: () => void;
}

const Ctx = createContext<TemaCtx | null>(null);

function inicial(): Tema {
  try {
    const guardado = localStorage.getItem(CLAVE) ?? localStorage.getItem(CLAVE_VIEJA);
    return guardado === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export function TemaProvider({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(inicial);

  useEffect(() => {
    document.documentElement.classList.toggle('light', tema === 'light');
    try {
      localStorage.setItem(CLAVE, tema);
      localStorage.removeItem(CLAVE_VIEJA);
    } catch {
      // Sin localStorage (modo privado): el tema solo dura la sesión.
    }
  }, [tema]);

  const alternar = useCallback(() => setTema((t) => (t === 'dark' ? 'light' : 'dark')), []);
  const valor = useMemo(() => ({ tema, alternar }), [tema, alternar]);
  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useTema(): TemaCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useTema fuera de TemaProvider');
  return c;
}
